import { useEffect, useState } from "react";
import { Loading } from "@/components/Loading";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Link } from "react-router-dom";
import { useBackOrigin } from "@/hooks/useBackTo";
import { Notice } from "@/components/Notice";
import { Dash } from "@/components/Dash";
import { Card } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/apiClient";
import { dashboard, type DashboardSummary } from "@/features/dashboard/api/dashboard.api";
import { useAuth } from "@/features/auth/auth";
import { formatMonth } from "@/utils/dates";

const chartConfig = {
  volume: { label: "Volume", color: "#334155" },
  netProfit: { label: "Net profit", color: "hsl(var(--primary))" },
} satisfies ChartConfig;

export default function DashboardPage() {
  const merchantOrigin = useBackOrigin();
  const { user } = useAuth();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dashboard.summary()
      .then((s) => { if (!cancelled) setSummary(s); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        else setError(e instanceof Error ? e.message : "Failed to load dashboard.");
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">
          Welcome, {user?.name?.split(" ")[0] ?? "there"}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {user?.role === "ADMIN"
            ? "Portfolio data across all agents."
            : "Here's how your merchant book is performing."}
        </p>
      </header>

      {forbidden && (
        <Notice tone="warn">
          You don't have permission to view the dashboard. Ask an admin to
          grant <code>10</code> (dashboard.view) on your role.
        </Notice>
      )}
      {error && !forbidden && <Notice tone="error">{error}</Notice>}
      {!forbidden && !error && summary === null && (
        <Loading>Loading dashboard…</Loading>
      )}

      {summary && (
        <>
          {(summary.myOpenTicketCount > 0 || summary.myOverdueTicketCount > 0) && (
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <PlateTile
                label="Open tickets assigned to me"
                value={summary.myOpenTicketCount}
                to="/tickets"
              />
              <PlateTile
                label="Overdue"
                value={summary.myOverdueTicketCount}
                to="/tickets"
                danger={summary.myOverdueTicketCount > 0}
              />
            </section>
          )}

          <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <KpiTile label="Merchants"      value={summary.merchantCount.toLocaleString()} />
            <KpiTile label="Open merchants" value={summary.openMerchantCount.toLocaleString()} />
            <KpiTile label="YTD volume"     value={fmtUsd(summary.ytdNetVolume)} accent />
            <KpiTile label="YTD net profit" value={fmtUsd(summary.ytdNetProfit)} />
            <KpiTile label="YTD commission" value={fmtUsd(summary.ytdUserCommission)} />
            <KpiTile label="YTD tickets"    value={summary.ytdTickets.toLocaleString()} />
            <KpiTile label="YTD chargebacks" value={summary.ytdChargebackCount.toLocaleString()} />
          </section>

          <Card className="p-4 mb-6">
            <header className="mb-3">
              <h2 className="text-sm font-semibold text-slate-700">Monthly volume & net profit</h2>
              <p className="text-xs text-slate-500">Last 12 months across your visible merchants.</p>
            </header>
            {summary.monthlyVolume.length === 0 ? (
              <p className="text-sm text-slate-500 italic py-12 text-center">
                No statement data yet. Once residual reports import, the
                chart fills in.
              </p>
            ) : (
              <ChartContainer config={chartConfig} className="!aspect-auto h-[280px] w-full">
                <BarChart accessibilityLayer data={summary.monthlyVolume.map(formatMonthlyRow)}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="monthLabel" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis tickFormatter={fmtCompact} tickLine={false} axisLine={false} width={48} />
                  <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar dataKey="volume" fill="var(--color-volume)" radius={4} />
                  <Bar dataKey="netProfit" fill="var(--color-netProfit)" radius={4} />
                </BarChart>
              </ChartContainer>
            )}
          </Card>

          <Card>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-700">
                Top merchants{" "}
                <span className="font-normal text-slate-400">(trailing 12 mo.)</span>
              </h2>
              <Link
                to="/merchants"
                className="text-xs font-medium text-brand-600 hover:underline"
              >
                View all merchants →
              </Link>
            </div>
            {summary.topMerchants.length === 0 ? (
              <p className="text-sm text-slate-500 italic px-4 py-8 text-center">
                No merchant volume yet. Top merchants appear once residual
                reports import.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-medium uppercase tracking-wide">Merchant</TableHead>
                    <TableHead className="text-xs font-medium uppercase tracking-wide">MID</TableHead>
                    <TableHead className="text-xs font-medium uppercase tracking-wide">Location</TableHead>
                    {user?.role === "ADMIN" && <TableHead className="text-xs font-medium uppercase tracking-wide">Owner</TableHead>}
                    <TableHead className="text-right text-xs font-medium uppercase tracking-wide">Net volume</TableHead>
                    <TableHead className="text-right text-xs font-medium uppercase tracking-wide">Net profit</TableHead>
                    <TableHead className="text-right text-xs font-medium uppercase tracking-wide">Commission</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.topMerchants.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>
                        <Link to={`/merchants/${m.id}`} state={merchantOrigin} className="font-medium text-slate-900 hover:underline">
                          {m.dbaName}
                        </Link>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-600">{m.mid}</TableCell>
                      <TableCell className="text-slate-600">{[m.city, m.state].filter(Boolean).join(", ") || <Dash />}</TableCell>
                      {user?.role === "ADMIN" && <TableCell className="text-slate-600">{m.ownerName}</TableCell>}
                      <TableCell className="text-right font-medium tabular-nums">{fmtUsd(m.netVolume)}</TableCell>
                      <TableCell className="text-right text-slate-700 tabular-nums">{fmtUsd(m.netProfit)}</TableCell>
                      <TableCell className="text-right text-slate-700 tabular-nums">{fmtUsd(m.commission)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </>
      )}
    </>
  );
}

function PlateTile({
  label,
  value,
  to,
  danger,
}: {
  label: string;
  value: number;
  to: string;
  danger?: boolean;
}) {
  return (
    <Link to={to} className="block">
      <Card
        className={cn(
          "p-3 transition-colors",
          danger ? "border-red-200 bg-red-50 text-red-900 hover:bg-red-100" : "hover:bg-slate-50",
        )}
      >
        <div className={cn("text-[10px] uppercase tracking-wider", danger ? "text-red-700" : "text-muted-foreground")}>
          {label}
        </div>
        <div className="text-2xl font-semibold tabular-nums mt-0.5">{value.toLocaleString()}</div>
      </Card>
    </Link>
  );
}

function KpiTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <Card className={cn("p-3 flex flex-col h-full", accent && "bg-slate-800 text-white border-transparent")}>
      <div className={cn("text-[10px] uppercase tracking-wider min-h-[2.5em]", accent ? "text-slate-300" : "text-muted-foreground")}>
        {label}
      </div>
      <div className="text-lg font-semibold tabular-nums mt-auto pt-1">{value}</div>
    </Card>
  );
}

function formatMonthlyRow(p: { month: string; volume: number; netProfit: number; commission: number }) {
  const monthLabel = formatMonth(p.month, { month: "short", year: "2-digit" });
  return { ...p, monthLabel };
}

function fmtUsd(value: number): string {
  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function fmtCompact(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000)     return `$${(value / 1_000).toFixed(0)}k`;
  return `$${value.toFixed(0)}`;
}
